// smartLocksHelpers.jsx
import { createConnectWebview, lockSmartLock, unlockSmartLock, updateLockName } from "../../services/SmartLocksService";
import { loadLocksHelper, pollLockStatusHelper } from "./smartLockHelpers";



export async function handleTransferLocks(lockIds, targetEmail, currentUser, setLoading) {
  if (!currentUser?.uid) {
    alert("User is not authenticated.");
    return;
  }
  if (!Array.isArray(lockIds) || lockIds.length === 0) {
    throw new Error("No locks selected for transfer.");
  }
  if (!targetEmail) {
    throw new Error("Recipient email is required.");
  }

  const base = process.env.REACT_APP_SEAM_API_URL ;
  if (!base) {
    throw new Error("Missing REACT_APP_SEAM_API_URL in environment.");
  }
  const url = `${base.replace(/\/$/, "")}/api/transfers/locks`;

  try {
    setLoading?.(true);

    const resp = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        fromUid: currentUser.uid,
        toEmail: targetEmail,
        lockIds,
      }),
    });

    const raw = await resp.text();
    let data = null;
    try {
      data = raw ? JSON.parse(raw) : null;
    } catch {
      data = { raw };
    }

    if (!resp.ok) {
      const msg = data?.message || data?.error || `Transfer failed (${resp.status})`;
      throw new Error(msg);
    }

    // Optional: toast/alert success
    // alert("Transfer initiated successfully.");

    return data; // let caller decide how to refresh UI
  } catch (err) {
    console.error("Transfer failed:", err);
    alert(err.message || "Failed to initiate transfer. Please try again.");
    throw err;
  } finally {
    setLoading?.(false);
  }
}
// Loads the initial list of locks with status
export async function loadLocks(currentUser, setLocks) {
  if (!currentUser?.uid) return;
  try {
    const locksWithStatus = await loadLocksHelper(currentUser.uid);
    setLocks(locksWithStatus);
  } catch (error) {
    console.error("Failed to load smart locks:", error);
  }
}

// Polls the locks for updated status
export async function pollLocks(currentUser, locks, setLocks) {
  if (!currentUser?.uid || locks.length === 0) return;
  try {
    const updatedLocks = await pollLockStatusHelper(locks, currentUser.uid);
    setLocks(updatedLocks);
  } catch (error) {
    console.error("Error polling lock statuses:", error);
  }
}

// Opens Seam Connect Webview to link new locks
export async function connectWebview(currentUser, setIframeUrl, setShowIframe) {
  if (!currentUser?.uid) {
    alert("User is not authenticated.");
    return;
  }
  const SMARTLOCKS_BASE_URL = process.env.REACT_APP_SMARTLOCKS_BASE_URL || "https://www.tenantguidedtours.com";
  try {
    const webviewUrl = await createConnectWebview({
      uid: currentUser.uid,
      redirectUrl: `${SMARTLOCKS_BASE_URL}/seam-success?uid=${currentUser.uid}`,
      failureUrl: `${SMARTLOCKS_BASE_URL}/seam-failure`,
    });
    navigator.clipboard.writeText(webviewUrl).catch((err) => {
      console.error("Failed to copy Webview URL: ", err);
    });
    setIframeUrl(webviewUrl);
    setShowIframe(true);
  } catch (error) {
    console.error("Failed to create Seam Connect Webview:", error);
    alert("Error connecting smart lock. Please try again.");
  }
}

// Handles lock or unlock action
export async function handleLockAction(lockId, action, currentUser, setLockActionLoading, locks, setLocks) {
  if (!currentUser?.uid) {
    alert("User is not authenticated.");
    return;
  }
  try {
    setLockActionLoading((prev) => ({ ...prev, [lockId]: true }));
    if (action === "lock") {
      await lockSmartLock(lockId, currentUser.uid);
    } else {
      await unlockSmartLock(lockId, currentUser.uid);
    }
    const updatedLocks = locks.map((lk) =>
      lk.lockId === lockId ? { ...lk, isLocked: action === "lock" } : lk
    );
    setLocks(updatedLocks);
  } catch (error) {
    console.error(`Failed to ${action} the lock:`, error);
  } finally {
    setLockActionLoading((prev) => ({ ...prev, [lockId]: false }));
  }
}

// Updates lock name in Seam & local state
export async function handleUpdateLockName(lockId, newLockName, currentUser, setLocks, setIsRenaming) {
  if (!newLockName.trim()) {
    throw new Error("Lock name cannot be empty");
  }
  if (!currentUser?.uid) {
    throw new Error("User is not authenticated");
  }
  setIsRenaming(true);
  try {
    // Update lock name in backend (Seam API and Firestore)
    await updateLockName(currentUser.uid, lockId, newLockName);
    
    // Update local state immediately for better UX
    setLocks((prevLocks) =>
      prevLocks.map((lk) =>
        lk.lockId === lockId ? { ...lk, deviceName: newLockName } : lk
      )
    );
  } catch (error) {
    console.error("Error updating lock name:", error);
    // Re-throw the error so calling code can handle it
    throw error;
  } finally {
    setIsRenaming(false);
  }
}

// Toggles "Manage Access Codes" dialog open
export function openAccessCodeDialog(lockId, setSelectedLockId, setOpenAccessCodeDialog) {
  setSelectedLockId(lockId);
  setOpenAccessCodeDialog(true);
}

// Toggles "Manage Access Codes" dialog closed
export function closeAccessCodeDialog(setOpenAccessCodeDialog) {
  setOpenAccessCodeDialog(false);
}

// Toggles "View Logs" dialog open
export function openLogsDialog(lockId, setOpenLogsForLockId) {
  setOpenLogsForLockId(lockId);
}

// Toggles "View Logs" dialog closed
export function closeLogsDialog(setOpenLogsForLockId) {
  setOpenLogsForLockId(null);
}

// Checks if a lock’s address matches accessCodeData.address
export function isMatchingLock(lock, accessCodeData) {
  if (!accessCodeData?.address) return false;

  const lockAddressFull = lock.deviceName.split(" - ")[0].toLowerCase().trim();
  const propertyAddressFull = accessCodeData.address.toLowerCase().trim();

  const lockParts = lockAddressFull.split(", ");
  const propertyParts = propertyAddressFull.split(", ");

  if (lockParts.length < 3 || propertyParts.length < 3) return false;

  const lockStreet = lockParts[0];
  const lockCity = lockParts[1];
  const lockStateZip = lockParts[2];
  const lockState = lockStateZip.split(" ")[0];

  const propertyStreet = propertyParts[0];
  const propertyCity = propertyParts[1];
  const propertyState = propertyParts[2];

  const stateMap = {
    florida: "fl",
    fl: "fl",
  };

  const streetMatch = lockStreet === propertyStreet;
  const cityMatch = lockCity === propertyCity;
  const stateMatch = stateMap[lockState] === stateMap[propertyState];

  return streetMatch && cityMatch && stateMatch;
}

// Formats lock name from rename details
export function formatLockName(details) {
  const { address, city, state, zip, lockName } = details;
  if (!address || !city || !state || !zip || !lockName) {
    throw new Error("All fields are required.");
  }
  return `${address}, ${city}, ${state} ${zip} - ${lockName}`;
}

// Parses a lock deviceName to extract address components and lock name
// Format: "address, city, state zip - lockName"
export function parseLockName(deviceName) {
  if (!deviceName) {
    return {
      address: '',
      city: '',
      state: '',
      zip: '',
      lockName: '',
    };
  }

  // Split by " - " to separate address from lock name
  const parts = deviceName.split(' - ');
  const addressPart = parts[0] || '';
  const lockNamePart = parts.length > 1 ? parts.slice(1).join(' - ').trim() : '';

  // Parse address part: "address, city, state zip"
  const addressComponents = addressPart.split(', ').map(s => s.trim());
  
  let address = '';
  let city = '';
  let state = '';
  let zip = '';

  if (addressComponents.length >= 3) {
    address = addressComponents[0] || '';
    city = addressComponents[1] || '';
    
    // Parse state and zip from the last component: "state zip"
    const stateZipPart = addressComponents[2] || '';
    const stateZipMatch = stateZipPart.match(/^([A-Z]{2})\s*(\d{5}(?:-\d{4})?)$/i);
    if (stateZipMatch) {
      state = stateZipMatch[1].toUpperCase();
      zip = stateZipMatch[2];
    } else {
      // Fallback: try to split by space
      const stateZipSplit = stateZipPart.split(/\s+/);
      if (stateZipSplit.length >= 2) {
        state = stateZipSplit[0].toUpperCase();
        zip = stateZipSplit.slice(1).join(' ');
      } else if (stateZipSplit.length === 1) {
        state = stateZipSplit[0].toUpperCase();
      }
    }
  } else if (addressComponents.length === 2) {
    address = addressComponents[0] || '';
    city = addressComponents[1] || '';
  } else if (addressComponents.length === 1) {
    address = addressComponents[0] || '';
  }

  return {
    address,
    city,
    state,
    zip,
    lockName: lockNamePart || deviceName, // If no lock name extracted, use full deviceName
  };
}